import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './ApprovalBanner.module.css';

/**
 * Owner-wide approval banner (rendered by MainLayout on every page).
 *
 * AB#2108 (Admin V1 P0-2) — founder-controlled go-live: after "Submit to Devanza"
 * the business is PendingApproval until a Devanza founder approves it; if the
 * founder asks for changes (Rejected) the owner sees the reason and resubmits from
 * My Business. Copy is translated (arabic-translator sign-off on AB#2108); the
 * founder-written reason is shown as-is in its own bidi-isolated element.
 *
 * Batch C (AB#2195) — while Devanza has suspended the salon, the owner sees why
 * customers can no longer find or book it, with Devanza's reason.
 */
const ApprovalBanner: React.FC = () => {
    const { businessStatus, rejectionReason, suspensionReason } = useOnboarding();
    const { t } = useLanguage();
    const navigate = useNavigate();

    if (businessStatus === 'pending_approval') {
        return (
            <div className={styles.banner} data-variant="pending" role="status">
                <div className={styles.bannerContent}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                    <span>{t('approval.banner.pending')}</span>
                </div>
            </div>
        );
    }

    if (businessStatus === 'suspended') {
        return (
            <div className={styles.banner} data-variant="rejected" role="status">
                <div className={styles.bannerContent}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="10" y1="9" x2="10" y2="15" />
                        <line x1="14" y1="9" x2="14" y2="15" />
                    </svg>
                    <div className={styles.bannerText}>
                        <span>{t('approval.banner.suspended')}</span>
                        {suspensionReason && (
                            <span className={styles.reason}>
                                <span>{t('approval.banner.reason')}</span>{' '}
                                <span dir="auto">{suspensionReason}</span>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (businessStatus === 'rejected') {
        return (
            <div className={styles.banner} data-variant="rejected" role="status">
                <div className={styles.bannerContent}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <div className={styles.bannerText}>
                        <span>{t('approval.banner.rejected')}</span>
                        {rejectionReason && (
                            <span className={styles.reason}>
                                <span>{t('approval.banner.reason')}</span>{' '}
                                <span dir="auto">{rejectionReason}</span>
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        className={styles.editBtn}
                        onClick={() => navigate('/my-business')}
                    >
                        {t('approval.banner.update')}
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

export default ApprovalBanner;
